import { BrowserProvider } from 'ethers';
import { WalletEvents } from '../iwallet';
import { Events } from './events';
import { RequestMethods } from './request';
/**
 * InjectedWallet is a class that implements the IWallet interface.
 * It is used to connect to a wallet that is injected into the browser and follows
 * the EIP-6963 standard.
 * @example
 * ```ts
 * const wallet = new InjectedWallet(window.ethereum)
 * ```
 */
export class InjectedWallet {
    constructor(provider) {
        this.provider = provider;
        this.listeners = {
            [WalletEvents.accountChanged]: [],
            [WalletEvents.networkChanged]: [],
            [WalletEvents.connect]: [],
            [WalletEvents.disconnect]: [],
        };
        const onAccountChanged = this.onAccountsChanged.bind(this);
        const onNetworkChanged = this.onNetworkChanged.bind(this);
        const onConnect = this.onConnect.bind(this);
        const onDisconnect = this.onDisconnect.bind(this);
        this.provider?.on(Events.ACCOUNTS_CHANGED, onAccountChanged);
        this.provider?.on(Events.CHAIN_CHANGED, onNetworkChanged);
        this.provider?.on(Events.CONNECT, onConnect);
        this.provider?.on(Events.DISCONNECT, onDisconnect);
        this.cleanupHandler = () => {
            this.provider?.removeListener(Events.ACCOUNTS_CHANGED, onAccountChanged);
            this.provider?.removeListener(Events.CHAIN_CHANGED, onNetworkChanged);
            this.provider?.removeListener(Events.CONNECT, onConnect);
            this.provider?.removeListener(Events.DISCONNECT, onDisconnect);
        };
    }
    cleanup() {
        this.cleanupHandler();
    }
    onAccountsChanged(accounts) {
        const [account] = accounts;
        this.listeners[WalletEvents.accountChanged].forEach((listener) => listener(account));
    }
    onNetworkChanged(chainId) {
        this.listeners[WalletEvents.networkChanged].forEach((listener) => listener(chainId));
    }
    onConnect() {
        this.listeners[WalletEvents.connect].forEach((listener) => listener());
    }
    onDisconnect() {
        this.listeners[WalletEvents.disconnect].forEach((listener) => listener());
    }
    isAvailable() {
        return Boolean(this.provider);
    }
    async connect() {
        await this.provider?.request({ method: RequestMethods.ETH_REQUEST_ACCOUNTS });
    }
    async disconnect() {
        await this.provider?.request({
            method: RequestMethods.WALLET_REVOKE_PERMISSIONS,
            params: [{ eth_accounts: {} }],
        });
    }
    async getAddress() {
        if (!this.isAvailable())
            throw new Error('Provider not available');
        const [address] = (await this.provider?.request({
            method: RequestMethods.ETH_ACCOUNTS,
        })) || [];
        return address;
    }
    async selectBlockchain(chainId) {
        const chainIdHex = `0x${chainId.toString(16)}`;
        await this.provider?.request({
            method: RequestMethods.WALLET_SWITCH_ETHEREUM_CHAIN,
            params: [{ chainId: chainIdHex }],
        });
    }
    on(event, listener) {
        this.listeners[event] = [...this.listeners[event], listener];
    }
    off(event, listener) {
        this.listeners[event] = this.listeners[event].filter((l) => l !== listener);
    }
    getProvider() {
        if (!this.provider)
            throw new Error('Provider not available');
        return new BrowserProvider(this.provider);
    }
    async getSigner() {
        if (!this.provider)
            throw new Error('Provider not available');
        const signer = await new BrowserProvider(this.provider).getSigner();
        return signer;
    }
    async transfer(amount, to) {
        const signer = await this.getSigner();
        const transaction = await signer.sendTransaction({
            to,
            value: amount,
        });
        await transaction.wait();
    }
}
//# sourceMappingURL=index.js.map